import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { usePageLoader } from '@/components/LogoLoader'
import { api, Capabilities } from '@/api/client'
import { formatValue } from '@/charts/palette'
import { useMe } from '@/features/auth/useAuth'
import { usePersistedState } from '@/lib/usePersistedState'
import { useIsPhone } from '@/lib/useMediaQuery'
import { FilterButton, FilterSheet } from '@/components/FilterSheet'
import { Select } from '@/components/Select'
import { fmtDate, useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { projectsMessages } from '@/i18n/messages/projects'
import { projectStatus } from './status'
import { EditProjectDialog } from './EditProjectDialog'
import { AddProjectRequestWizard } from './AddProjectRequestWizard'
import { ProjectRequests, type ProjectRequest } from './ProjectRequests'

const PAGE_SIZES = [25, 50, 100]

interface ProjectListItem {
  projectId: string
  name: string | null
  code: string | null
  codeInternal: string | null
  providerName: string | null
  rom: string | null
  status: number | null
  crewDays: number
  metres: number
  revenueSub: number
  lastWorked: string | null
  planedMetres: number | null
  contractValue: number | null
  tbStart: string | null
  tbEnd: string | null
}

type SortKey =
  | 'name' | 'status' | 'provider' | 'rom'
  | 'crewDays' | 'metres' | 'progress' | 'sub' | 'schedule' | 'lastWorked'

const progressOf = (p: ProjectListItem): number | null =>
  p.planedMetres && p.planedMetres > 0 ? p.metres / p.planedMetres : null

type View = 'all' | 'active' | 'missing' | 'requests'

/** Days between two yyyy-MM-dd strings. */
const daysBetween = (a: string, b: string) =>
  Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000)

/** Worked within ~a month of the most recent work in the data (robust to a lagging refresh). */
const isActive = (p: ProjectListItem, latest: string | null) =>
  !!p.lastWorked && !!latest && daysBetween(p.lastWorked, latest) <= 31

/** Which core fields AU never filled in — drives the "Missing data" view. */
type MissingKey = 'provider' | 'rom' | 'status' | 'missingPlannedM' | 'missingContract' | 'missingTbDates'

function missingFields(p: ProjectListItem): MissingKey[] {
  const gaps: MissingKey[] = []
  if (!p.providerName) gaps.push('provider')
  if (!p.rom) gaps.push('rom')
  if (p.status == null) gaps.push('status')
  if (!p.planedMetres) gaps.push('missingPlannedM')
  if (!p.contractValue) gaps.push('missingContract')
  if (!p.tbStart && !p.tbEnd) gaps.push('missingTbDates')
  return gaps
}

/** The comparable value for each sort key. null always sorts to the bottom, either direction. */
function sortValue(p: ProjectListItem, key: SortKey): number | string | null {
  switch (key) {
    case 'name': return (p.name ?? '').toLowerCase()
    case 'status': return p.status ?? null
    case 'provider': return (p.providerName ?? '').toLowerCase()
    case 'rom': return (p.rom ?? '').toLowerCase()
    case 'crewDays': return p.crewDays
    case 'metres': return p.metres
    case 'progress': return progressOf(p)
    case 'sub': return p.revenueSub
    case 'schedule': return p.tbStart
    case 'lastWorked': return p.lastWorked
  }
}

export function ProjectsPage() {
  const t = useT(projectsMessages)
  const tc = useT(commonMessages)
  const [search, setSearch] = useState('')
  const [provider, setProvider] = useState('')
  const [rom, setRom] = useState('')
  const [status, setStatus] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'metres', dir: 'desc' })
  const [editing, setEditing] = useState<ProjectListItem | null>(null)
  const [view, setView] = useState<View>('all')
  const [showWizard, setShowWizard] = useState(false)
  const isPhone = useIsPhone()
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = usePersistedState('projects:pageSize', 25)

  const resetPage = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setPage(1) }

  const { data: me } = useMe()
  const canEdit = me?.capabilities.includes(Capabilities.EditAufmass) ?? false

  const { data, isPending } = useQuery<ProjectListItem[]>({
    queryKey: ['projects', search],
    queryFn: () =>
      api.get<ProjectListItem[]>(
        `/api/projects/${search.trim() ? `?search=${encodeURIComponent(search.trim())}` : ''}`,
      ),
    // Typing a search keeps the current rows up until the new ones land, instead of blanking
    // the table on every keystroke.
    placeholderData: keepPreviousData,
  })

  const { data: requests } = useQuery<ProjectRequest[]>({
    queryKey: ['project-requests'],
    queryFn: () => api.get<ProjectRequest[]>('/api/projects/requests'),
  })
  const openRequests = (requests ?? []).filter((r) => r.status === 'open').length

  // Filter dimensions, derived from the current result so they only ever offer real values.
  const providers = useMemo(
    () => [...new Set((data ?? []).map((p) => p.providerName).filter(Boolean))].sort() as string[],
    [data],
  )
  const roms = useMemo(
    () => [...new Set((data ?? []).map((p) => p.rom).filter(Boolean))].sort() as string[],
    [data],
  )
  const statuses = useMemo(
    () => [...new Set((data ?? []).map((p) => p.status).filter((s): s is number => s != null))]
      .sort((a, b) => a - b),
    [data],
  )

  // The most recent work day across ALL projects — the reference point for "active this month",
  // so a data refresh that lags a few days doesn't empty the Active tab.
  const latestWork = useMemo(
    () => (data ?? []).reduce<string | null>(
      (m, p) => (p.lastWorked && (!m || p.lastWorked > m) ? p.lastWorked : m), null),
    [data],
  )

  // Dropdown filters first; the view (All / Active / Missing) narrows on top of that.
  const base = useMemo(
    () => (data ?? []).filter(
      (p) =>
        (provider === '' || p.providerName === provider) &&
        (rom === '' || p.rom === rom) &&
        (status === '' || String(p.status) === status),
    ),
    [data, provider, rom, status],
  )

  const counts = useMemo(() => ({
    all: base.length,
    active: base.filter((p) => isActive(p, latestWork)).length,
    missing: base.filter((p) => missingFields(p).length > 0).length,
  }), [base, latestWork])

  const rows = useMemo(() => {
    const viewed = view === 'active'
      ? base.filter((p) => isActive(p, latestWork))
      : view === 'missing'
        ? base.filter((p) => missingFields(p).length > 0)
        : base
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...viewed].sort((a, b) => {
      const av = sortValue(a, sort.key)
      const bv = sortValue(b, sort.key)
      if (av == null && bv == null) return 0
      if (av == null) return 1 // nulls last
      if (bv == null) return -1
      const r = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv))
      return r * dir
    })
  }, [base, view, latestWork, sort])

  const toggleSort = (key: SortKey) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }))
    setPage(1)
  }

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const rowOffset = (currentPage - 1) * pageSize
  const pageRows = rows.slice(rowOffset, rowOffset + pageSize)
  const from = rows.length === 0 ? 0 : rowOffset + 1
  const to = rowOffset + pageRows.length

  // The wordmark loader covers the first load only; with keepPreviousData a search never
  // re-enters it, so the search box is never swapped out from under the cursor.
  const loader = usePageLoader(isPending, t('loadingProjects'))
  if (loader) return loader

  // [label, control] — inline on desktop, one labelled field each in the phone sheet.
  const selects: [string, ReactNode][] = [
    [t('provider'), (
      <Select key="provider" value={provider} onChange={resetPage(setProvider)} aria-label={t('provider')}
        className="max-lg:w-full lg:w-fit lg:min-w-[10rem]"
        empty={{ value: '', label: t('allProviders') }}
        options={providers.map((v) => ({ value: v, label: v }))} />
    )],
    [t('rolloutManager'), (
      <Select key="rom" value={rom} onChange={resetPage(setRom)} aria-label={t('rolloutManager')}
        className="max-lg:w-full lg:w-fit lg:min-w-[10rem]"
        empty={{ value: '', label: t('allRoms') }}
        options={roms.map((v) => ({ value: v, label: v }))} />
    )],
    [t('status'), (
      <Select key="status" value={status} onChange={resetPage(setStatus)} aria-label={t('status')}
        className="max-lg:w-full lg:w-fit lg:min-w-[8rem]"
        empty={{ value: '', label: t('allStatuses') }}
        options={statuses.map((s) => ({ value: String(s), label: projectStatus(s).label }))} />
    )],
  ]

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{tc('navProjects')}</h1>
          <p className="mt-1 text-sm text-neutral-500">
            {t('subtitle')}
          </p>
        </div>
        {/* Desktop: the filters inline. Phone: search + a Filters button; the three dropdowns
            live in a full-screen sheet instead of taking half the screen above the table. */}
        <div className="flex w-full flex-wrap items-center gap-2 lg:w-auto">
          {!isPhone && selects.map(([, control]) => control)}
          <input
            type="search"
            enterKeyHint="search"
            value={search}
            onChange={(e) => resetPage(setSearch)(e.target.value)}
            placeholder={t('searchPlaceholder')}
            aria-label={t('searchProjects')}
            className="min-w-0 flex-1 rounded-lg border border-neutral-300 px-2.5 py-1.5 text-sm max-lg:h-11 max-lg:rounded-xl lg:w-56 lg:flex-none dark:border-neutral-700 dark:bg-neutral-950"
          />
          {isPhone && (
            <FilterButton
              count={[provider, rom, status].filter(Boolean).length}
              onClick={() => setFiltersOpen(true)}
              className="shadow-sm"
            />
          )}
          {canEdit && (
            <button
              onClick={() => setShowWizard(true)}
              className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-500 max-lg:h-11 max-lg:w-full max-lg:rounded-xl"
            >
              {t('addProject')}
            </button>
          )}
        </div>
      </header>

      {/* Views: everything AU shows, just what's being worked now, or just what's incomplete. */}
      <div className="flex flex-wrap items-center gap-1.5 max-lg:-mx-4 max-lg:flex-nowrap max-lg:overflow-x-auto max-lg:px-4 max-lg:py-1 max-lg:[scrollbar-width:none] max-lg:[&>*]:shrink-0">
        <Tab active={view === 'all'} onClick={() => resetPage(setView)('all')}>{tc('all')} <Count>{counts.all}</Count></Tab>
        <Tab active={view === 'active'} onClick={() => resetPage(setView)('active')}>
          {t('tabActive')} <Count>{counts.active}</Count>
        </Tab>
        <Tab active={view === 'missing'} onClick={() => resetPage(setView)('missing')}>
          {t('tabMissing')} <Count>{counts.missing}</Count>
        </Tab>
        <Tab active={view === 'requests'} onClick={() => resetPage(setView)('requests')}>
          {t('tabRequests')} <Count>{openRequests}</Count>
        </Tab>
        {view === 'active' && latestWork && (
          <span className="ml-1 text-xs text-neutral-400">{t('workedWithin', { date: fmtDate(latestWork) })}</span>
        )}
      </div>

      {view === 'requests' ? (
        <ProjectRequests requests={requests ?? []} />
      ) : (
      <div className="rounded-2xl bg-white ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-xs text-neutral-500 dark:border-neutral-800">
                <th className="w-10 px-3 py-2 text-right font-medium">#</th>
                <Th col="name" sort={sort} onSort={toggleSort}>{t('colProject')}</Th>
                <Th col="status" sort={sort} onSort={toggleSort}>{t('status')}</Th>
                <Th col="provider" sort={sort} onSort={toggleSort}>{t('provider')}</Th>
                <Th col="rom" sort={sort} onSort={toggleSort}>ROM</Th>
                <Th col="crewDays" sort={sort} onSort={toggleSort} align="right">{t('crewDays')}</Th>
                <Th col="metres" sort={sort} onSort={toggleSort} align="right">{tc('metres')}</Th>
                <Th col="progress" sort={sort} onSort={toggleSort}>{t('colProgress')}</Th>
                <Th col="sub" sort={sort} onSort={toggleSort} align="right">{t('colSub')}</Th>
                <Th col="schedule" sort={sort} onSort={toggleSort}>{t('colSchedule')}</Th>
                <Th col="lastWorked" sort={sort} onSort={toggleSort}>{t('lastWorked')}</Th>
                <th className="px-3 py-2 text-right font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((p, i) => {
              const st = projectStatus(p.status)
              return (
                <tr
                  key={p.projectId}
                  className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50 dark:border-neutral-800/60 dark:hover:bg-neutral-800/40"
                >
                  <td className="tabular px-3 py-2 text-right text-xs text-neutral-400">{rowOffset + i + 1}</td>
                  <td className="px-3 py-2">
                    <Link
                      to={`/projects/${p.projectId}`}
                      className="font-medium text-brand-700 hover:underline dark:text-brand-100"
                    >
                      {p.name ?? t('unnamed')}
                    </Link>
                    {(p.codeInternal || p.code) && (
                      <span className="ml-2 text-xs text-neutral-400">
                        {[p.code, p.codeInternal].filter(Boolean).join(' · ')}
                      </span>
                    )}
                    {p.crewDays === 0 && (
                      <span className="ml-2 text-xs text-neutral-400">{t('noWorkYet')}</span>
                    )}
                    {view === 'missing' && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {missingFields(p).map((m) => (
                          <span key={m}
                            className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                            {m === 'rom' ? 'ROM' : t(m)}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className="inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium"
                      style={{ color: st.color, backgroundColor: `color-mix(in srgb, ${st.color} 14%, transparent)` }}
                    >
                      {st.label}
                    </span>
                  </td>
                  <td className="px-3 py-2">{p.providerName ?? <Muted>—</Muted>}</td>
                  <td className="px-3 py-2">{p.rom ?? <Muted>—</Muted>}</td>
                  <td className="tabular px-3 py-2 text-right">{formatValue(p.crewDays, '')}</td>
                  <td className="tabular px-3 py-2 text-right">
                    {p.metres ? formatValue(p.metres, '') : <Muted>0</Muted>}
                  </td>
                  <td className="px-3 py-2"><Progress p={p} /></td>
                  <td className="tabular px-3 py-2 text-right">
                    {p.revenueSub ? formatValue(p.revenueSub, 'EUR') : <Muted>—</Muted>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-neutral-500">
                    {p.tbStart || p.tbEnd
                      ? `${fmtDate(p.tbStart)} – ${fmtDate(p.tbEnd)}`
                      : <Muted>{t('notPlanned')}</Muted>}
                  </td>
                  <td className="px-3 py-2 text-neutral-500">{fmtDate(p.lastWorked)}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => setEditing(p)}
                      className="rounded-md px-2 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-600/30 hover:bg-brand-50 dark:text-brand-100 dark:ring-brand-100/20 dark:hover:bg-brand-700/20"
                    >
                      {tc('edit')}
                    </button>
                  </td>
                </tr>
              )
            })}
            {!isPending && rows.length === 0 && (
              <tr>
                <td colSpan={12} className="px-3 py-10 text-center text-sm text-neutral-500">
                  {view === 'active' ? t('emptyActive')
                    : view === 'missing' ? t('emptyMissing')
                    : t('emptyAll')}
                </td>
              </tr>
            )}
            </tbody>
          </table>
        </div>
        <Pager
          page={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          pageSizes={PAGE_SIZES}
          total={rows.length}
          from={from}
          to={to}
          onPage={setPage}
          onPageSize={resetPage(setPageSize)}
        />
      </div>
      )}

      {editing && (
        <EditProjectDialog
          projectId={editing.projectId}
          name={editing.name}
          onClose={() => setEditing(null)}
        />
      )}
      {showWizard && <AddProjectRequestWizard onClose={() => setShowWizard(false)} />}
      {isPhone && (
        <FilterSheet
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          title={t('filterProjects')}
          subtitle={t('projectsMatch', { count: rows.length })}
          onReset={() => { setProvider(''); setRom(''); setStatus(''); setPage(1) }}
          resetDisabled={!provider && !rom && !status}
          doneLabel={t('showProjects', { count: rows.length })}
        >
          <div className="space-y-5 p-4">
            {selects.map(([label, control]) => (
              <label key={label} className="block">
                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-neutral-500">{label}</span>
                {control}
              </label>
            ))}
          </div>
        </FilterSheet>
      )}
    </div>
  )
}

function Th({
  col, sort, onSort, align, children,
}: {
  col: SortKey
  sort: { key: SortKey; dir: 'asc' | 'desc' }
  onSort: (k: SortKey) => void
  align?: 'right'
  children: React.ReactNode
}) {
  const active = sort.key === col
  return (
    <th className={`px-3 py-2 font-medium ${align === 'right' ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className={`inline-flex items-center gap-1 hover:text-neutral-800 dark:hover:text-neutral-200 ${
          active ? 'text-neutral-800 dark:text-neutral-200' : ''
        } ${align === 'right' ? 'flex-row-reverse' : ''}`}
      >
        {children}
        <span aria-hidden className="text-[10px]">{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  )
}

function Progress({ p }: { p: ProjectListItem }) {
  const t = useT(projectsMessages)
  const ratio = progressOf(p)
  if (ratio == null) {
    return (
      <span className="text-xs text-neutral-400 dark:text-neutral-600">
        {t('noPlan', { metres: formatValue(p.metres, 'm') })}
      </span>
    )
  }
  const pct = Math.round(ratio * 100)
  const over = ratio > 1
  const color = over ? 'var(--viz-serious)' : 'var(--viz-good)'
  return (
    <div className="flex items-center gap-2" title={t('progressTitle', { done: formatValue(p.metres, 'm'), planned: formatValue(p.planedMetres!, 'm') })}>
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800">
        <div className="h-full rounded-full" style={{ width: `${Math.min(ratio, 1) * 100}%`, backgroundColor: color }} />
      </div>
      <span className="tabular w-9 text-right text-xs" style={{ color }}>{pct}%</span>
    </div>
  )
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-neutral-400 dark:text-neutral-600">{children}</span>
}

function Tab({ active, onClick, children }:
  { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg px-3 py-1.5 text-sm font-medium max-lg:py-2 ${
        active
          ? 'bg-brand-600 text-white'
          : 'text-neutral-600 ring-1 ring-neutral-300 hover:bg-neutral-100 dark:text-neutral-300 dark:ring-neutral-700 dark:hover:bg-neutral-800'
      }`}
    >
      {children}
    </button>
  )
}

function Count({ children }: { children: React.ReactNode }) {
  return <span className="tabular ml-1 text-xs opacity-70">{children}</span>
}

function Pager({
  page, totalPages, pageSize, pageSizes, total, from, to, onPage, onPageSize,
}: {
  page: number
  totalPages: number
  pageSize: number
  pageSizes: number[]
  total: number
  from: number
  to: number
  onPage: (p: number) => void
  onPageSize: (n: number) => void
}) {
  const t = useT(projectsMessages)
  const tc = useT(commonMessages)
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-200 px-3 py-2 text-xs text-neutral-500 dark:border-neutral-800">
      <div className="flex items-center gap-2">
        <Select
          value={String(pageSize)}
          onChange={(v) => onPageSize(Number(v))}
          aria-label={tc('rowsPerPage')}
          size="sm"
          className="w-fit"
          options={pageSizes.map((n) => ({ value: String(n), label: t('perPage', { n }) }))}
        />
        <span className="tabular">
          {total === 0 ? t('noneOf', { n: 0, total: 0 }) : t('rangeOf', { from, to, total })}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          aria-label={tc('previousPage')}
          className="rounded px-2 py-1 hover:bg-neutral-100 disabled:opacity-30 dark:hover:bg-neutral-800"
        >
          ‹
        </button>
        <span className="tabular text-neutral-700 dark:text-neutral-200">{page} / {totalPages}</span>
        <button
          type="button"
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages}
          aria-label={tc('nextPage')}
          className="rounded px-2 py-1 hover:bg-neutral-100 disabled:opacity-30 dark:hover:bg-neutral-800"
        >
          ›
        </button>
      </div>
    </div>
  )
}
